import { QueryClient } from "@tanstack/react-query";
import { BaseLogo } from "../../core/models/entities/BaseLogo";
import { Design } from "../../core/models/entities/Design";
import { actAsCacheKey } from "../../contexts/actAsStore";
import { enviroment } from "../../enviroment";
import {
  PriceRowFields,
  priceFieldsDiffer,
} from "../../utils/functions/cartPriceCheck";
import { hasAdjustedPrices, writePreservingFreshness } from "./designCachePatch";
import {
  isLogoDetailFromList,
  isLogoDetailQueryKey,
  logoQueryKeyScope,
} from "./logoDetailCache";

// "The price follows to the list" (flag REACT_APP_CART_PRICE_CHECK_LIST_SYNC_
// ENABLED, requires REACT_APP_CART_PRICE_CHECK_ENABLED; Nichlas 12/9: "when
// the price is updated in the cart it is updated on the item too, when you
// get to the list" - and "it must not cost performance or speed").
//
// The cart price check re-reads ONE cart entry (GetLogo / GetDesign) when
// the API reports a logo "updated"; that answer lands in the DETAIL entry
// (["logos", actAs, id] / ["designs", actAs, id]) and useCartSync patches
// the cart line. The cached LIST pages under ["logos"] (logo list, customer
// logo tab, infinite lists) and ["designs"] (design list) are never
// rewritten by that: the QueryClient has refetchOnMount off globally, the
// list's own return-to-list refetch is only armed by the logo editor, and
// the pending-state patch (logoDetailCache.patchLogoRowsFromDetail) touches
// pending rows only. So the list the user returns to draws the OLD price
// until its next refetch (60 s / 5 min staleTime, but only on a query
// change) - proven 12/9 (cartPriceCheckListSync.hole sketch).
//
// This module copies ONLY the price fields of a landed detail into the row
// with the same id in every cached list page of the current act-as scope:
//   logo row:   product.prices (what the logo list / popup stub / cart
//               start price read) and product.additionalFees when the
//               detail carries them (the cart's fee lines and useCartSync's
//               self-heal read the row a line is added from)
//   design row: product.totalPrices, product.products, and each placed
//               designLogos[].logo.product.prices INSIDE the design row
//               (the design's own answer - see price CONTEXT below; never
//               the placed logo's own logo-list row or detail entry)
// Everything else in a row stays
// (sku, pending flags, storage, images, approval). Pure cache writes - 0
// calls, no invalidation, no refetch, no seeding of absent entries; a list
// that is not cached is fetched on first view as today. Written with
// writePreservingFreshness: the page keeps its own dataUpdatedAt and a
// pending invalidation, so it is refetched exactly when it would have been
// without the patch. Rows and pages are never mutated in place (new objects
// for the row, its product, the designLogos array and the page); the
// price-row arrays are COPIED into the patched row (ProductDetailsLogo
// sorts a cart line's `product.prices` in place, so a list row must never
// share that array with the cart line / detail entry - a shared array would
// make every reader's order depend on whoever sorted last). Sub-flag off =
// every function returns 0 / the same reference.
//
// Price CONTEXT (skeptic round 2, verified in the API, main 0735b5d): a
// logo's price rows depend on WHO asks. GetLogo/GetAllLogos
// (LogoService.MarkupPrices) look the serial up in NAV with the vendor's
// NAV number only (the parent's for a "Bill-to Customer" branch), apply a
// DUMMY vendor commission (no markup), convert to the caller's own currency
// and compute AdjustedCustomerPrice from the LOGO's customer organization's
// commission (none for a global logo). GetDesign (DesignService.AdjustPrice)
// looks the same serial up with [design vendor NAV, design CUSTOMER NAV]
// (customer-specific NAV rows win - the TRANSFER-discount case of 10/9),
// applies the design vendor's REAL commission profile, converts to the
// design vendor's currency and computes AdjustedCustomerPrice from the
// DESIGN's customer - explicitly not the logo's. So a placed logo's rows
// inside a landed design are the price of that logo IN THAT DESIGN, not the
// rows GetLogo/GetAllLogos answer with - for a vendor as much as for an
// admin, with or without act-as. They are therefore copied ONLY into the
// design's own list row (designLogos[].logo inside it, which the design
// list reads in the same context) and NEVER into the logo lists, the
// customer logo tab or the logo's own detail entry (the popup's source): a
// placed logo's logo-list row and popup refresh through the logo's own
// GetLogo alone - the cart line's when it is also a logo line, else the
// ONE the check fires on "updated" (useCartPriceCheck.landPlacedLogoInLists,
// skeptic round 3), which lands in the detail entry and calls
// patchLogoPricesInLists with it. Belt and braces, a row whose rows are
// priced in another currency than the fresh rows is never written.
//
// Copies, not references (round 3): the fee set that travels with the
// prices is copied too - new fee objects with copied price rows - because
// mapToAdditionalFee (the design list's select re-maps a patched raw row)
// writes minQuantity = 1 into a fee's first price row in place, and a list
// row must never share a mutable object with the cart line / detail entry.

// Structural view of the price carriers: a mapped BaseLogo (ProductRef /
// ProductPrice), a raw LogoDto (ProductRefDto / PriceDto), a mapped Design
// (MultiProductRef / Price) and a raw DesignDto (MultiProductRefDto /
// PriceDto) carry the same fields - the mappers are 1:1 spreads (mapToPrice
// only defaults currency), so one patch serves the raw list rows and the
// mapped cart entries alike and a patched raw row maps idempotently.
interface PricedProduct {
  sku?: string;
  prices?: readonly PriceRowFields[] | null;
  additionalFees?: unknown;
}

interface PricedLogo {
  id: number;
  product?: PricedProduct | null;
}

interface PricedMultiProduct {
  mul?: string;
  products?: readonly PricedProduct[] | null;
  totalPrices?: readonly PriceRowFields[] | null;
}

interface PricedDesignLogo {
  logoId: number;
  logo?: PricedLogo | null;
}

interface PricedDesign {
  id: number;
  product?: PricedMultiProduct | null;
  designLogos?: readonly PricedDesignLogo[] | null;
}

function isPagedResults<T>(data: unknown): data is { results: T[] } {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as { results?: unknown }).results)
  );
}

function isInfiniteData(data: unknown): data is { pages: unknown[] } {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as { pages?: unknown }).pages)
  );
}

// The currency a set of rows is priced in (the first row that carries one),
// or undefined when none does. PriceRowFields deliberately omits currency
// (the cart never compares it); the rows carry it structurally (PriceDto /
// ProductPrice / Price).
function rowsCurrency(
  rows: readonly PriceRowFields[] | null | undefined,
): string | undefined {
  for (const row of rows ?? []) {
    const currency = (row as { currency?: unknown } | null)?.currency;
    if (typeof currency === "string" && currency.length > 0) return currency;
  }
  return undefined;
}

// The cart's row-by-row rule (priceFieldsDiffer) is index-sensitive: the
// same rows in another order would count as a change, be written, and - as
// a design row's totalPrices/products - be read back as a change by the
// next comparison. Two row sets that hold the same rows in ANY order are
// the same prices here: no write. Sorted COPIES are compared (nothing is
// reordered in place; a real change is written in the order the detail
// answered with, which is what the cart line and the popup show), and the
// sort runs only when the cheap index-wise check already said "differs".
function sortKeyNumber(value: unknown): number {
  return typeof value === "number" && !Number.isNaN(value) ? value : 0;
}

function compareRows(a: PriceRowFields, b: PriceRowFields): number {
  const typeA = String((a as { type?: unknown }).type ?? "");
  const typeB = String((b as { type?: unknown }).type ?? "");
  if (typeA !== typeB) return typeA < typeB ? -1 : 1;
  return (
    sortKeyNumber(a.minQuantity) - sortKeyNumber(b.minQuantity) ||
    sortKeyNumber(a.adjustedPrice) - sortKeyNumber(b.adjustedPrice) ||
    sortKeyNumber(a.adjustedUnitPrice) - sortKeyNumber(b.adjustedUnitPrice) ||
    sortKeyNumber(a.adjustedCustomerPrice) -
      sortKeyNumber(b.adjustedCustomerPrice)
  );
}

function pricesDiffer(
  current: readonly PriceRowFields[] | null | undefined,
  fresh: readonly PriceRowFields[] | null | undefined,
): boolean {
  const rows = current ?? undefined;
  const freshRows = fresh ?? undefined;
  if (!priceFieldsDiffer(rows, freshRows)) return false;
  if ((rows?.length ?? 0) !== (freshRows?.length ?? 0)) return true;
  return priceFieldsDiffer(
    [...(rows ?? [])].sort(compareRows),
    [...(freshRows ?? [])].sort(compareRows),
  );
}

// --- Logo rows -------------------------------------------------------------

// A fee set as COPIES: new fee objects, each with a copied price-row array
// of copied rows (mapToAdditionalFee mutates a row in place - see the
// module note). Anything that is not an array comes back as it is.
function copyFees(fees: unknown): unknown {
  if (!Array.isArray(fees)) return fees;
  return fees.map((fee) => {
    if (!fee || typeof fee !== "object") return fee;
    const prices = (fee as { prices?: unknown }).prices;
    return Array.isArray(prices)
      ? { ...fee, prices: prices.map((p) => (p && typeof p === "object" ? { ...p } : p)) }
      : { ...fee };
  });
}

/**
 * The list row with the detail's price rows - or the very same row when
 * there is nothing to copy: another logo, a detail without a single ADJUSTED
 * row (persisted-only / empty = no information, same rule as the design
 * cache patch), rows priced in another currency than the row's own (another
 * pricing context - never written), or identical prices in any order
 * (reference check = no dispatch). Written: product.prices (a copy of the
 * detail's rows, in the detail's order) and
 * product.additionalFees - as a COPY (copyFees) - when the detail carries a NON-EMPTY set (the cart
 * totals fees from the logo a line was added from, and useCartSync's
 * self-heal compares price rows only, so fees travel with the prices; an
 * empty set carries no information - mapToProductRef defaults a missing
 * set to [] - and the row keeps its own); a row without a product gets one
 * with the detail's sku.
 */
export function patchLogoRowPrices<T extends PricedLogo>(
  row: T,
  detail: PricedLogo,
): T {
  if (!row || row.id !== detail.id) return row;
  const fresh = detail.product?.prices ?? undefined;
  if (!hasAdjustedPrices(fresh)) return row;
  const rowCurrency = rowsCurrency(row.product?.prices);
  const freshCurrency = rowsCurrency(fresh);
  if (rowCurrency && freshCurrency && rowCurrency !== freshCurrency) return row;
  if (!pricesDiffer(row.product?.prices, fresh)) return row;
  const prices = [...(fresh as readonly PriceRowFields[])];
  const freshFees = detail.product?.additionalFees;
  const additionalFees =
    Array.isArray(freshFees) && freshFees.length > 0
      ? copyFees(freshFees)
      : row.product?.additionalFees ?? [];
  const product = row.product
    ? { ...row.product, prices, additionalFees }
    : { sku: detail.product?.sku ?? "", additionalFees, prices };
  return { ...row, product } as T;
}

function patchLogoRows<T extends PricedLogo>(rows: T[], detail: PricedLogo): T[] {
  let changed = false;
  const next = rows.map((row) => {
    const patched = patchLogoRowPrices(row, detail);
    if (patched !== row) changed = true;
    return patched;
  });
  return changed ? next : rows;
}

/**
 * A cached logo list entry with the detail's prices applied to the row of
 * the same id: a paged list ({results}) or an infinite list ({pages:
 * [{results}]}). Any other shape, or nothing to change, comes back as the
 * very same reference; an untouched page inside an infinite list keeps its
 * reference too.
 */
export function patchCachedLogoPricesData(
  data: unknown,
  detail: PricedLogo,
): unknown {
  if (isPagedResults<PricedLogo>(data)) {
    const results = patchLogoRows(data.results, detail);
    return results === data.results ? data : { ...data, results };
  }
  if (isInfiniteData(data)) {
    let changed = false;
    const pages = data.pages.map((page) => {
      if (!isPagedResults<PricedLogo>(page)) return page;
      const results = patchLogoRows(page.results, detail);
      if (results === page.results) return page;
      changed = true;
      return { ...page, results };
    });
    return changed ? { ...data, pages } : data;
  }
  return data;
}

/**
 * Copies the price rows of a landed logo detail (a cart line's own GetLogo -
 * never a placed logo out of a GetDesign, see price CONTEXT above) into the
 * row with the same id in every cached LIST entry under ["logos"] of the
 * current act-as scope. `landedAtMs` is the detail entry's dataUpdatedAt: a
 * list NEWER than that is server truth read after the detail and is left
 * alone.
 *
 * Never written: detail entries (the source itself; a detail entry is only
 * ever written by its own GetLogo - the popup and the cart line read it),
 * another act-as scope's lists (prices are scope-dependent - currency, #603), the
 * design picker's unscoped entry, a list newer than the detail, the popup's
 * list-row stub (not a detail), a detail without adjusted rows, and an
 * entry that would not change. Writes preserve the entry's freshness stamp
 * and invalidation. Returns the number of entries written. Sub-flag off = 0,
 * nothing touched.
 */
export function patchLogoPricesInLists(
  queryClient: QueryClient,
  detail: BaseLogo | null | undefined,
  landedAtMs: number,
): number {
  if (!enviroment.cartPriceCheckListSyncEnabled) return 0;
  if (!detail || typeof detail.id !== "number") return 0;
  if (isLogoDetailFromList(detail)) return 0;
  if (!hasAdjustedPrices(detail.product?.prices)) return 0;
  const scope = actAsCacheKey();
  let written = 0;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["logos"] })
    .forEach((query) => {
      const key = query.queryKey;
      if (isLogoDetailQueryKey(key)) return;
      if (logoQueryKeyScope(key) !== scope) return;
      if (landedAtMs > 0 && query.state.dataUpdatedAt > landedAtMs) return;
      const current = query.state.data;
      if (current == null) return;
      const next = patchCachedLogoPricesData(current, detail);
      if (next === current) return;
      writePreservingFreshness(queryClient, query, next);
      written += 1;
    });
  return written;
}

// --- Design rows -----------------------------------------------------------

// Same rule as designCachePatch.productHasAdjustedPrices, on the structural
// view: at least one adjusted row in totalPrices or in any products[i].
function multiProductHasAdjustedPrices(
  product: PricedMultiProduct | null | undefined,
): boolean {
  if (!product) return false;
  if (hasAdjustedPrices(product.totalPrices)) return true;
  return (product.products ?? []).some((p) => hasAdjustedPrices(p?.prices));
}

// products[] patched per index (prices only, copied); a different length
// cannot be aligned and takes the fresh set - as COPIES (new product objects
// with copied price arrays and copied fee sets: the fresh set is the cart's
// own design entry, and a list row must never share an array or a mutable
// fee object with it - see the module note)
// - but only a fresh set that carries information (non-empty, at least one
// adjusted row): GetDesign answers products: [] when the API sends null, and
// a fresh design with adjusted totalPrices but no products must not erase
// the row's products. `current` comes back when nothing changed.
function patchProducts(
  current: readonly PricedProduct[] | null | undefined,
  fresh: readonly PricedProduct[],
): readonly PricedProduct[] | null | undefined {
  const rows = current ?? [];
  if (rows.length !== fresh.length) {
    return fresh.length > 0 && fresh.some((p) => hasAdjustedPrices(p?.prices))
      ? fresh.map((p) =>
          p
            ? {
                ...p,
                prices: p.prices ? [...p.prices] : p.prices,
                additionalFees: copyFees(p.additionalFees),
              }
            : p,
        )
      : current;
  }
  let changed = false;
  const next = rows.map((row, index) => {
    const freshRows = fresh[index]?.prices ?? undefined;
    if (!hasAdjustedPrices(freshRows)) return row;
    if (!pricesDiffer(row?.prices, freshRows)) return row;
    changed = true;
    return { ...row, prices: [...(freshRows as readonly PriceRowFields[])] };
  });
  return changed ? next : current;
}

/**
 * The design list row with the fresh design's prices - three independent
 * parts, each only when the fresh side carries ADJUSTED rows and differs
 * (in any order - see pricesDiffer):
 * product.totalPrices, product.products (per index) and every placed
 * designLogos[].logo (matched on logoId, via patchLogoRowPrices). Nothing
 * changed = the very same row; otherwise a new row with a new product
 * and/or a new designLogos array in which only the changed placements are
 * new objects.
 */
export function patchDesignRowPrices<T extends PricedDesign>(
  row: T,
  fresh: PricedDesign,
): T {
  if (!row || row.id !== fresh.id) return row;
  const currentProduct = row.product ?? undefined;
  let product = currentProduct;
  const freshProduct = fresh.product ?? undefined;
  if (freshProduct) {
    const freshTotal = freshProduct.totalPrices ?? undefined;
    if (
      hasAdjustedPrices(freshTotal) &&
      pricesDiffer(product?.totalPrices, freshTotal)
    ) {
      product = {
        ...(product ?? { mul: freshProduct.mul ?? "", products: [] }),
        totalPrices: [...(freshTotal as readonly PriceRowFields[])],
      };
    }
    const freshRows = freshProduct.products ?? undefined;
    if (freshRows && multiProductHasAdjustedPrices(freshProduct)) {
      const products = patchProducts(product?.products, freshRows);
      if (products !== product?.products) {
        product = {
          ...(product ?? { mul: freshProduct.mul ?? "", totalPrices: [] }),
          products,
        };
      }
    }
  }

  const currentLogos = row.designLogos ?? undefined;
  let designLogos = currentLogos;
  const freshLogos = fresh.designLogos ?? [];
  if (currentLogos && freshLogos.length > 0) {
    let changed = false;
    const next = currentLogos.map((placed) => {
      if (!placed?.logo) return placed;
      const freshPlaced = freshLogos.find((f) => f?.logoId === placed.logoId);
      if (!freshPlaced?.logo) return placed;
      const logo = patchLogoRowPrices(placed.logo, freshPlaced.logo);
      if (logo === placed.logo) return placed;
      changed = true;
      return { ...placed, logo };
    });
    if (changed) designLogos = next;
  }

  if (product === currentProduct && designLogos === currentLogos) return row;
  const next = { ...row } as T;
  if (product !== currentProduct) next.product = product;
  if (designLogos !== currentLogos) next.designLogos = designLogos;
  return next;
}

/**
 * A cached design list page ({results} of raw DesignDto rows) with the
 * fresh design's prices applied to the row of the same id, or the very same
 * reference when nothing changes.
 */
export function patchCachedDesignPricesData(
  data: unknown,
  fresh: PricedDesign,
): unknown {
  if (!isPagedResults<PricedDesign>(data)) return data;
  let changed = false;
  const results = data.results.map((row) => {
    const patched = patchDesignRowPrices(row, fresh);
    if (patched !== row) changed = true;
    return patched;
  });
  return changed ? { ...data, results } : data;
}

/**
 * Copies the prices of a landed cart design (GetDesign) into the row with
 * the same id in every cached design LIST page (["designs", scope, qs]) of
 * the current act-as scope. Detail entries (the source) are never written;
 * a page newer than the landing (dataUpdatedAt > landedAtMs) is server
 * truth and left alone; an entry that would not change is not written.
 * Writes preserve freshness and invalidation. Returns the number of pages
 * written. Sub-flag off = 0, nothing touched.
 */
export function patchDesignPricesInLists(
  queryClient: QueryClient,
  fresh: Design | null | undefined,
  landedAtMs: number,
): number {
  if (!enviroment.cartPriceCheckListSyncEnabled) return 0;
  if (!fresh || typeof fresh.id !== "number") return 0;
  if (
    !multiProductHasAdjustedPrices(fresh.product) &&
    !(fresh.designLogos ?? []).some((dl) =>
      hasAdjustedPrices(dl?.logo?.product?.prices),
    )
  ) {
    return 0;
  }
  const scope = actAsCacheKey();
  let written = 0;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["designs"] })
    .forEach((query) => {
      const key = query.queryKey;
      if (key.length !== 3 || key[1] !== scope || typeof key[2] !== "string") {
        return;
      }
      if (landedAtMs > 0 && query.state.dataUpdatedAt > landedAtMs) return;
      const current = query.state.data;
      if (current == null) return;
      const next = patchCachedDesignPricesData(current, fresh);
      if (next === current) return;
      writePreservingFreshness(queryClient, query, next);
      written += 1;
    });
  return written;
}
